import { addDays, format, isValid, parse, parseISO } from 'date-fns';
import { zhTW } from 'date-fns/locale';

/*
 * 以 'YYYY-MM-DD' 字串（date key）代表「哪一天」，一律為本地時區。
 *
 * 解析與格式化都走 date-fns，不自行切字串或組 Date：`new Date('2026-08-05')` 依規格
 * 會被當成 UTC 午夜，在 UTC+8 顯示成前一天；date-fns 的 `parse`／`parseISO` 對
 * 只有日期的字串以本地時區解讀。
 */

const DATE_KEY_PATTERN = 'yyyy-MM-dd';

/** Date → 'YYYY-MM-DD'（本地時區） */
export function toDateKey(date: Date): string {
  return format(date, DATE_KEY_PATTERN);
}

/** 'YYYY-MM-DD' → 本地時區當日 00:00 的 Date */
export function fromDateKey(dateKey: string): Date {
  return parse(dateKey, DATE_KEY_PATTERN, new Date());
}

/**
 * ISO 8601 字串 → date key。
 *
 * 給 `IonDatetime` 這類回傳完整 ISO 的來源使用：它在只選日期時仍可能帶上時間與
 * 時區位移，直接拿去比對 date key 會對不上。
 *
 * @returns 無法解析時回傳 null，由呼叫端決定要不要沿用原值
 */
export function toDateKeyFromISO(value: string): string | null {
  const date = parseISO(value);
  return isValid(date) ? toDateKey(date) : null;
}

/** 往前（負數）或往後移動幾天，跨月與跨年由 date-fns 處理。 */
export function shiftDateKey(dateKey: string, days: number): string {
  return toDateKey(addDays(fromDateKey(dateKey), days));
}

export function todayKey(now = new Date()): string {
  return toDateKey(now);
}

/** 'YYYY-MM-DD' → '8月5日' */
export function formatMonthDay(dateKey: string): string {
  return format(fromDateKey(dateKey), 'M月d日');
}

/** 'YYYY-MM-DD' → '星期三' */
export function formatWeekday(dateKey: string): string {
  return format(fromDateKey(dateKey), 'EEEE', { locale: zhTW });
}

/**
 * 動態消息的時間標示：一小時內以分鐘計、當天以小時計，更早的顯示日期。
 */
export function formatRelativeTime(isoTime: string, now = new Date()): string {
  const time = parseISO(isoTime);
  const elapsedMinutes = Math.floor((now.getTime() - time.getTime()) / 60_000);

  if (elapsedMinutes < 1) return '剛剛';
  if (elapsedMinutes < 60) return `${elapsedMinutes} 分鐘前`;
  if (elapsedMinutes < 24 * 60)
    return `${Math.floor(elapsedMinutes / 60)} 小時前`;

  return format(time, 'M月d日');
}
